import { Checkbox, Input, Select } from "antd";
import { AddDeleteButton } from "components/SeasonView/components/AddDeleteButton";
import { useSharedColumnBuilder } from "hooks/useSharedColumnBuilder";
import { useSportConfiguration } from "hooks/useSportConfiguration";
import { debounce } from "lodash-es";
import { useIntl } from "react-intl";

type Props = {
  onAdd: (record: any) => void;
  onDelete: (record: any) => void;
  getAlreadyAdded: (record: any) => any;
  checkIfRecordBeingBusy: (record: any) => boolean;
};

export const useAddPeopleRosterColumns = ({ onAdd, onDelete, getAlreadyAdded, checkIfRecordBeingBusy }: Props) => {
  const { formatMessage } = useIntl();
  const createDebouncedHandler = (lambda: (value: any) => void) =>
    debounce((value) => {
      lambda(value);
    }, 500);
  const { createPersonNameColumn, createPersonDobColumn, createPersonNationalityColumn, createPersonStatusColumn } =
    useSharedColumnBuilder();
  const { configuration } = useSportConfiguration();

  const checkIfAlreadyAdded = (record: any) => !!getAlreadyAdded(record);

  return [
    createPersonNameColumn({ checkIfAlreadyAdded }),
    createPersonDobColumn({ withSorter: false }),
    createPersonNationalityColumn(),
    createPersonStatusColumn(),
    {
      title: formatMessage({ id: "columns.roster.number" }),
      align: "center",
      dataIndex: "number",
      width: 75,
      render: (_, record) => {
        const alreadyAdded = getAlreadyAdded(record);
        if (alreadyAdded) {
          return alreadyAdded.number;
        }

        return (
          <Input
            style={{ textAlign: "center" }}
            disabled={checkIfRecordBeingBusy(record)}
            defaultValue={record.number}
            onInput={createDebouncedHandler((e) => {
              record.number = e.target.value?.toString() ?? "";
            })}
          />
        );
      },
    },
    {
      title: formatMessage({ id: "columns.roster.position" }),
      dataIndex: "position",
      width: 120,
      align: "center",
      render: (_, record) => {
        const alreadyAdded = getAlreadyAdded(record);
        if (alreadyAdded) {
          return alreadyAdded.position ? formatMessage({ id: `position.${alreadyAdded.position}` }) : "";
        }

        return (
          <Select
            style={{ width: "150px" }}
            showSearch
            defaultValue={record.position}
            disabled={checkIfRecordBeingBusy(record)}
            placeholder={formatMessage({
              id: "select.rosterPosition.placeholder",
            })}
            filterOption={(input, option) => (option?.label?.toLowerCase() ?? "").includes(input?.toLowerCase())}
            onChange={(x) => {
              record.position = x;
            }}
            options={configuration.rosterPositions.map((x) => ({
              value: x,
              label: formatMessage({ id: `position.${x}` }),
              dcPersonId: record?.dcPersonId,
              personId: record?.personId,
            }))}
          />
        );
      },
    },
    {
      title: formatMessage({ id: "columns.roster.isCaptain" }),
      dataIndex: "isCaptian",
      width: 75,
      align: "center",
      render: (_, record) => {
        const alreadyAdded = getAlreadyAdded(record);
        if (alreadyAdded) {
          return alreadyAdded.isCaptain ? <i className="fa-solid fa-check"></i> : "";
        }

        return (
          <Checkbox
            disabled={checkIfRecordBeingBusy(record)}
            defaultChecked={record.isCaptain}
            onChange={(e) => {
              record.isCaptain = e.target.checked;
            }}
          />
        );
      },
    },
    {
      render: (_, record) => (
        <AddDeleteButton
          isLoading={checkIfRecordBeingBusy(record)}
          isAdd={!checkIfAlreadyAdded(record)}
          onAdd={() => onAdd(record)}
          onDelete={() => onDelete(record)}
        />
      ),
      width: 50,
    },
  ];
};
